import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useAuthorization } from '../hooks/use-authorization';
import { useCredentials, type Credential, type StoreCredentialInput, type RotateCredentialInput } from '../state/credential-state';
import { useToast } from '../state/toast-state';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button, buttonVariants } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Spinner } from '../components/ui/spinner';
import { EmptyState } from '../components/ui/empty-state';
import { ErrorState } from '../components/ui/error-state';
import { InlineForm, type InlineFormField } from '../components/ui/inline-form';
import { cn } from '../lib/utils';

/**
 * Credentials Page — System_Admin only.
 * Displays credentials as a responsive card grid with inline rotation actions.
 * Non-System_Admin users see an authorization error.
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.8, 9.1, 9.2, 9.3
 */

const CREDENTIAL_FORM_FIELDS: readonly InlineFormField[] = [
  { name: 'domains', label: 'Domains (comma-separated)', placeholder: 'example.com, example.org', required: true },
  { name: 'apiToken', label: 'API Token', placeholder: 'Enter API token', required: true },
  { name: 'description', label: 'Description', placeholder: 'Production token for DNS zone', required: false },
];

export function CredentialsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { isSystemAdmin } = useAuthorization();
  const { state, loadCredentials, storeCredential, rotateCredential, isConnecting } = useCredentials();
  const { showToast } = useToast();
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [rotateTarget, setRotateTarget] = React.useState<Credential | null>(null);
  const [newRotationToken, setNewRotationToken] = React.useState('');

  React.useEffect(() => {
    if (isSystemAdmin) {
      loadCredentials();
    }
  }, [isSystemAdmin, loadCredentials]);

  // Authorization error for non-System_Admin users (Req 4.8)
  if (!isSystemAdmin) {
    return (
      <section aria-labelledby="credentials-heading">
        <h2 id="credentials-heading" className="sr-only">Credentials</h2>
        <Alert variant="destructive">
          <AlertTitle>Authorization Error</AlertTitle>
          <AlertDescription>
            You do not have permission to access credential management.
            This feature is restricted to System Administrators.
          </AlertDescription>
        </Alert>
      </section>
    );
  }

  const handleCardClick = (credentialId: string): void => {
    navigate(`/admin-console/credentials/${credentialId}`);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, credentialId: string): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      navigate(`/admin-console/credentials/${credentialId}`);
    }
  };

  const handleRotateClick = (e: React.MouseEvent, credential: Credential): void => {
    e.stopPropagation();
    setRotateTarget(credential);
  };

  const handleRotateConfirm = async (): Promise<void> => {
    if (!rotateTarget || !newRotationToken.trim()) return;

    const input: RotateCredentialInput = {
      credentialId: rotateTarget.credentialId,
      newApiToken: newRotationToken,
    };
    const targetDescription = rotateTarget.description || rotateTarget.credentialId;

    try {
      await rotateCredential(input);
      setRotateTarget(null);
      setNewRotationToken('');
      showToast(`Credential "${targetDescription}" rotation initiated.`, 'success');
    } catch {
      showToast('Credential rotation failed. You can try again — the token is still in the input.', 'error');
    }
  };

  const handleRotateCancel = (): void => {
    setRotateTarget(null);
    setNewRotationToken('');
  };

  const handleAddSubmit = async (data: Record<string, string>): Promise<void> => {
    const input: StoreCredentialInput = {
      domains: (data['domains'] ?? '').split(',').map((d) => d.trim()).filter(Boolean),
      apiToken: data['apiToken'] ?? '',
      description: data['description'] ?? '',
    };
    await storeCredential(input);
    setShowAddForm(false);
    showToast('Credential stored successfully.', 'success');
  };

  const handleAddCancel = (): void => {
    setShowAddForm(false);
  };

  const handleShowAddForm = (): void => {
    setShowAddForm(true);
  };

  return (
    <section aria-labelledby="credentials-heading">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 id="credentials-heading" className="text-2xl font-semibold text-foreground">
            Cloudflare Credentials
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage API tokens used for DNS operations.
          </p>
        </div>
        <Button onClick={handleShowAddForm} disabled={showAddForm}>
          Add Credential
        </Button>
      </div>

      {showAddForm && (
        <div className="mb-6">
          <InlineForm
            fields={CREDENTIAL_FORM_FIELDS}
            onSubmit={handleAddSubmit}
            onCancel={handleAddCancel}
            submitLabel="Store Credential"
          />
        </div>
      )}

      {isConnecting ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12" aria-busy="true">
          <Spinner label="Connecting..." />
          <p className="text-sm text-muted-foreground">Establishing connection to the server...</p>
        </div>
      ) : state.isLoading ? (
        <Spinner label="Loading credentials..." />
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={loadCredentials} />
      ) : state.credentials.length === 0 ? (
        <EmptyState
          title="No credentials stored"
          description="There are no credentials stored yet. Add your first credential to get started with DNS management."
          actionLabel="Add Credential"
          onAction={handleShowAddForm}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {state.credentials.map((cred) => (
            <Card
              key={cred.credentialId}
              role="button"
              tabIndex={0}
              aria-label={`Credential ${cred.description || cred.credentialId}`}
              onClick={() => handleCardClick(cred.credentialId)}
              onKeyDown={(e) => handleCardKeyDown(e, cred.credentialId)}
              className={cn(
                'min-h-[44px] cursor-pointer transition-colors hover:bg-accent/50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-lg">
                    {cred.description || `Credential ${cred.credentialId.slice(0, 8)}`}
                  </CardTitle>
                  <Badge variant={cred.status === 'active' ? 'success' : 'warning'}>
                    {cred.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 gap-1 text-sm">
                  <div className="flex items-center gap-2">
                    <dt className="text-muted-foreground">Domains:</dt>
                    <dd>{cred.domains.join(', ')}</dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <dt className="text-muted-foreground">Last validated:</dt>
                    <dd>{new Date(cred.validatedAt).toLocaleDateString()}</dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={cred.status === 'rotating'}
                    onClick={(e) => handleRotateClick(e, cred)}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    Rotate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Rotation Confirmation Dialog with API token input */}
      <AlertDialog.Root
        open={rotateTarget !== null}
        onOpenChange={(isOpen) => { if (!isOpen) handleRotateCancel(); }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay
            className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            aria-hidden="true"
          />
          <AlertDialog.Content
            className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
          >
            <div className="flex flex-col space-y-2 text-center sm:text-left">
              <AlertDialog.Title className="text-lg font-semibold">
                Rotate Credential
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-muted-foreground">
                {rotateTarget
                  ? `You are about to rotate the credential "${rotateTarget.description || rotateTarget.credentialId}". Enter a new API token to proceed.`
                  : ''}
              </AlertDialog.Description>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="rotation-token" className="text-sm font-medium">
                New API Token
              </label>
              <Input
                id="rotation-token"
                type="password"
                value={newRotationToken}
                onChange={(e) => setNewRotationToken(e.target.value)}
                placeholder="Enter new API token"
                aria-label="New API token for rotation"
              />
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <AlertDialog.Cancel
                className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0')}
                onClick={handleRotateCancel}
              >
                Cancel
              </AlertDialog.Cancel>
              <AlertDialog.Action
                className={cn(buttonVariants({ variant: 'destructive' }))}
                onClick={handleRotateConfirm}
                disabled={!newRotationToken.trim()}
              >
                Rotate
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </section>
  );
}
